import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { setToken, setUser, setCurrentProject } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const res = await axios.post(`${API}/auth/login`, { username, password });
            setToken(res.data.access_token);
            setUser(res.data.user);
            if (res.data.user.project_ids?.length > 0) {
                setCurrentProject(res.data.user.project_ids[0]);
            }
            navigate("/", { replace: true });
        } catch (err) {
            setError(err.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">
                {/* Logo */}
                <div className="flex items-center gap-3 mb-8">
                    <Package size={32} className="text-orange-500" />
                    <span className="text-2xl font-bold tracking-widest text-white font-mono uppercase">
                        StockOS
                    </span>
                </div>

                <div className="bg-zinc-900 border border-zinc-800 p-6">
                    <h1 className="text-sm font-semibold uppercase tracking-wider text-zinc-300 mb-6">
                        Sign In
                    </h1>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-1">
                            <Label htmlFor="username" className="text-xs text-zinc-400 uppercase tracking-wider">
                                Username
                            </Label>
                            <Input
                                id="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                autoFocus
                                autoComplete="username"
                                className="rounded-none bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-orange-500"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label htmlFor="password" className="text-xs text-zinc-400 uppercase tracking-wider">
                                Password
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                className="rounded-none bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-orange-500"
                            />
                        </div>

                        {error && (
                            <p className="text-xs text-red-400 border border-red-900 bg-red-950/30 px-3 py-2">
                                {error}
                            </p>
                        )}

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full rounded-none uppercase tracking-wider bg-orange-500 hover:bg-orange-600 text-white"
                        >
                            {loading ? "Signing in..." : "Sign In"}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
